import { ApiBaseUrl, DomainSharing, Environment, UseLoginLocal } from "configs";

const isSandbox = ApiBaseUrl?.includes("sandbox");

const config = {
  prefix: "TUM",
  // eslint-disable-next-line no-nested-ternary
  accessToken: isSandbox ? (UseLoginLocal ? "AccessToken" : "ACT") : "dhr_act",
  // eslint-disable-next-line no-nested-ternary
  refreshToken: isSandbox
    ? UseLoginLocal
      ? "RefreshToken"
      : "RFT"
    : "dhr_rft",
  tenantCode: "tenantCode",
  orgId: "orgId",
} as const;

/**
 * Saving data to localStorage.
 */
export const saveLocalStorage = (name: string, value: unknown) => {
  if (typeof window === "undefined" ?? !window.localStorage) return;
  localStorage.setItem(`${config.prefix}:${name}`, JSON.stringify(value));
};

/**
 * Load data from localStorage.
 */
export const loadLocalStorage = (name: string) => {
  if (typeof window === "undefined" ?? !window.localStorage) return null;
  const serialized = localStorage.getItem(`${config.prefix}:${name}`);
  if (serialized === null) return null;
  return JSON.parse(serialized) as string;
};

/**
 * Remove data from localStorage.
 */
export const removeLocalStorage = (name: string) => {
  if (typeof window === "undefined" ?? !window.localStorage) return;
  localStorage.removeItem(`${config.prefix}:${name}`);
};

/**
 * Load data from sessionStore.
 */
export const loadSessionStorage = (name: string) => {
  if (typeof window === "undefined" ?? !window.sessionStorage) return null;
  const serialized = sessionStorage.getItem(`${config.prefix}:${name}`);
  if (serialized === null) return null;
  return JSON.parse(serialized) as string;
};

/**
 * Saving data to localStorage.
 */
export const saveSessionStorage = (name: string, value: unknown) => {
  if (typeof window === "undefined" ?? !window.sessionStorage) return;
  sessionStorage.setItem(`${config.prefix}:${name}`, JSON.stringify(value));
};

/**
 * Remove data from localStorage.
 */
export const removeSessionStorage = (name: string) => {
  if (typeof window === "undefined" ?? !window.sessionStorage) return;
  sessionStorage.removeItem(`${config.prefix}:${name}`);
};

export const loadTenantCode = () => loadSessionStorage(config.tenantCode);
export const clearTenantCode = () => removeSessionStorage(config.tenantCode);
export const saveTenantCode = (tenantCode: string) =>
  saveSessionStorage(config.tenantCode, tenantCode);

export const loadOrgId = () => loadSessionStorage(config.orgId);
export const clearOrgId = () => removeSessionStorage(config.orgId);
export const saveOrgId = (tenantCode: string) =>
  saveSessionStorage(config.orgId, tenantCode);

export const getDefaultOrgId = (userId: number) => {
  return loadLocalStorage(String(userId));
};

export const setDefaultOrgId = (userId: number, orgId: number) => {
  return saveLocalStorage(String(userId), orgId);
};
export const clearDefaultOrgId = (userId: number) => {
  return removeLocalStorage(String(userId));
};

/**
 * Saving data to cookieStorage.
 */
export const saveCookieStorage = (params: {
  name: string;
  value: unknown;
  exDays?: number;
  path?: string;
  domain?: string;
  httpOnly?: boolean;
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
}) => {
  const {
    name,
    value,
    exDays,
    domain,
    path = "/",
    httpOnly,
    secure,
    sameSite,
  } = params;
  const cName = `${Environment as string}:${name}`;
  const d = new Date();
  const cExpires =
    typeof exDays === "number"
      ? `expires=${(
          d.setTime(
            d.getTime() + exDays * 24 * 60 * 60 * 1000
          ) as unknown as Date
        ).toUTCString()};`
      : "";
  const cDomain = domain ? `domain=${domain};` : "";
  const cPath = path ? `path=${path};` : "";
  const cHttpOnly = httpOnly ? "HttpOnly;" : "";
  const cSecure = secure ? "Secure;" : "";
  const cSameSite = sameSite ? `SameSite=${sameSite};` : "";
  document.cookie = `${cName}=${
    value as string
  };${cExpires}${cDomain}${cPath}${cHttpOnly}${cSecure}${cSameSite}`;
};

/**
 * Load data from cookieStorage.
 */
export const loadCookieStorage = (name: string) => {
  const cName = `${Environment as string}:${name}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(cName) === 0) {
      return c.substring(cName.length, c.length);
    }
  }
  return "";
};

/**
 * Remove data from sessionStorage.
 */
export const removeCookieStorage = (params: {
  name: string;
  path?: string;
  domain?: string;
  httpOnly?: boolean;
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
}) => {
  const { name, domain, path = "/", httpOnly, secure, sameSite } = params;
  const cName = `${Environment as string}:${name}`;
  const cExpires = "expires=Thu, 01 Jan 1970 00:00:00 UTC;";
  const cDomain = domain ? `domain=${domain};` : "";
  const cPath = path ? `path=${path};` : "";
  const cHttpOnly = httpOnly ? "HttpOnly;" : "";
  const cSecure = secure ? "Secure;" : "";
  const cSameSite = sameSite ? `SameSite=${sameSite};` : "";
  document.cookie = `${cName}=;${cExpires}${cDomain}${cPath}${cHttpOnly}${cSecure}${cSameSite}`;
};

export const saveAccessToken = (accessToken: string) =>
  UseLoginLocal
    ? saveLocalStorage(config.accessToken, accessToken)
    : saveCookieStorage({
        name: config.accessToken,
        value: accessToken,
        domain: DomainSharing,
        secure: true,
        sameSite: "Strict",
      });
export const loadAccessToken = () =>
  UseLoginLocal
    ? loadLocalStorage(config.accessToken)
    : loadCookieStorage(config.accessToken);
export const clearAccessToken = () =>
  UseLoginLocal
    ? removeLocalStorage(config.accessToken)
    : removeCookieStorage({
        name: config.accessToken,
        domain: DomainSharing,
        secure: true,
        sameSite: "Strict",
      });

export const saveRefreshToken = (refreshToken: string) =>
  UseLoginLocal
    ? saveLocalStorage(config.refreshToken, refreshToken)
    : saveCookieStorage({
        name: config.refreshToken,
        value: refreshToken,
        domain: DomainSharing,
        secure: true,
        sameSite: "Strict",
      });

export const loadRefreshToken = () =>
  UseLoginLocal
    ? loadLocalStorage(config.refreshToken)
    : loadCookieStorage(config.refreshToken);

export const clearRefreshToken = () =>
  UseLoginLocal
    ? removeLocalStorage(config.refreshToken)
    : removeCookieStorage({
        name: config.refreshToken,
        domain: DomainSharing,
        secure: true,
        sameSite: "Strict",
      });
